import Grid from "@mui/material/Grid";
import { MenuItem, Select, FormControl, InputLabel, Skeleton } from "@mui/material";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import PeopleIcon from "@mui/icons-material/People";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import apiService from "services/api";

const FILTER_OPTIONS = [
  { value: "", label: "All time" },
  { value: "THIS_MONTH", label: "This month" },
  { value: "LAST_MONTH", label: "Last month" },
  { value: "THIS_YEAR", label: "This year" },
  { value: "LAST_7_DAYS", label: "Last 7 days" },
  { value: "LAST_30_DAYS", label: "Last 30 days" },
];

function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("");
  const [reportsBarChartData, setReportBarChartData] = useState({
    labels: [],
    datasets: { label: "HMT Sales", data: [] },
  });

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterType ? { filterType } : {};
      const response = await apiService.getDashboard(params);
      const result = response?.data;
      if (!result?.success) {
        throw new Error(result?.message || "Failed to load dashboard");
      }
      setData(result.data);
      const monthly = result.data?.planAmoutByMonth || {};
      setReportBarChartData({
        labels: Object.keys(monthly),
        datasets: { label: "HMT Sales (₹)", data: Object.values(monthly) },
      });
    } catch (error) {
      toast.error(
        error?.response?.data?.message || error?.message || "Failed to load dashboard"
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const totalTurnover =
    data?.totalTurnOver ??
    data?.profitByMonthlyPlan?.[0]?.totalAmount ??
    0;

  const chartItems = [
    {
      icon: { color: "info", component: <CurrencyRupeeIcon /> },
      label: "HMT TURNOVER",
      progress: { content: `₹${totalTurnover}`, percentage: 100 },
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar handleRefresh={fetchDashboard} />
      <SoftBox py={3}>
        <SoftBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <SoftBox>
            <SoftTypography variant="h5" fontWeight="bold">
              HMT Sales Dashboard
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Monthly HMT plan revenue and member activity
            </SoftTypography>
          </SoftBox>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Period</InputLabel>
            <Select
              value={filterType}
              label="Period"
              onChange={(e) => setFilterType(e.target.value)}
            >
              {FILTER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </SoftBox>

        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              {loading ? (
                <Skeleton variant="rounded" sx={{ borderRadius: "12px" }} height={80} />
              ) : (
                <MiniStatisticsCard
                  title={{ text: "HMT Turnover" }}
                  count={`₹${totalTurnover}`}
                  percentage={{ color: "success", text: "" }}
                  icon={{ color: "info", component: <CurrencyRupeeIcon /> }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              {loading ? (
                <Skeleton variant="rounded" sx={{ borderRadius: "12px" }} height={80} />
              ) : (
                <MiniStatisticsCard
                  title={{ text: "Active Members" }}
                  count={data?.activeMembers ?? "—"}
                  percentage={{ color: "success", text: "" }}
                  icon={{ color: "info", component: <PeopleIcon /> }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              {loading ? (
                <Skeleton variant="rounded" sx={{ borderRadius: "12px" }} height={80} />
              ) : (
                <MiniStatisticsCard
                  title={{ text: "Total Members" }}
                  count={data?.memberCount ?? "—"}
                  percentage={{ color: "success", text: "" }}
                  icon={{ color: "info", component: <AccountCircleIcon /> }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              {loading ? (
                <Skeleton variant="rounded" sx={{ borderRadius: "12px" }} height={80} />
              ) : (
                <MiniStatisticsCard
                  title={{ text: "New Members Today" }}
                  count={data?.newTodayMembers ?? "—"}
                  percentage={{ color: "success", text: "" }}
                  icon={{ color: "info", component: <AccountCircleIcon /> }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              {loading ? (
                <Skeleton variant="rounded" sx={{ borderRadius: "12px" }} height={80} />
              ) : (
                <MiniStatisticsCard
                  title={{ text: "Plan Sales (This Month)" }}
                  count={data?.planCount ?? "—"}
                  percentage={{ color: "success", text: "" }}
                  icon={{ color: "info", component: "shopping_cart" }}
                />
              )}
            </Grid>
          </Grid>
        </SoftBox>

        <SoftBox mb={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              {loading ? (
                <Skeleton variant="rounded" sx={{ borderRadius: "12px" }} height={320} />
              ) : (
                <ReportsBarChart
                  title="HMT Sales by Month"
                  chart={reportsBarChartData}
                  items={chartItems}
                />
              )}
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
}

export default Dashboard;
